import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ModulesTableSimple, ModuleDetailsDrawer, CreateModuleDrawer, EditModuleDrawer } from '../../components/security';
import { ModulesService, type Module } from '../../services/security/modules.service';
import { NotificationToast } from '../../components/ui/NotificationToast';

export const ModulesPage: React.FC = () => {
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [isCreateModuleOpen, setIsCreateModuleOpen] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState<Module | null>(null);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activoFilter, setActivoFilter] = useState<boolean | undefined>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para notificaciones
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [updateNotificationConfig, setUpdateNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Cerrar modales cuando se desmonta el componente
  useEffect(() => {
    return () => {
      setSelectedModule(null);
    };
  }, []);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Resetear página cuando cambie el término de búsqueda debounced
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, activoFilter]);

  // Cerrar dropdown cuando se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Query para obtener módulos
  const {
    data: modulesData,
    isLoading: isLoadingModules,
    error: modulesError,
    refetch: refetchModules,
    isFetching: isFetchingModules
  } = useQuery({
    queryKey: ['modules', page, limit, debouncedSearchTerm, activoFilter],
    queryFn: async () => {
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        return await ModulesService.searchModules(debouncedSearchTerm.trim(), page, limit, activoFilter);
      }
      return await ModulesService.getModules(page, limit, activoFilter);
    },
    staleTime: 30000, // 30 segundos
    refetchOnWindowFocus: false,
    retry: 2
  });

  const modules = modulesData?.data || [];
  const meta = modulesData?.meta;

  // Mantener foco en el input después de la búsqueda
  useEffect(() => {
    if (!isFetchingModules && searchTerm && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isFetchingModules, searchTerm]);


  // Handlers
  const handleCreateModule = () => {
    setIsCreateModuleOpen(true);
  };

  const handleCloseCreateModule = () => {
    setIsCreateModuleOpen(false);
  };

  const handleCreateModuleSuccess = () => {
    refetchModules();
    setIsCreateModuleOpen(false);
    setUpdateNotificationConfig({
      title: '¡Éxito!',
      message: 'Módulo creado exitosamente',
      type: 'success'
    });
    setShowUpdateNotification(true);
  };

  const handleViewModule = (module: Module) => {
    setSelectedModule(module);
  };

  const handleCloseModuleDetails = () => {
    setSelectedModule(null);
  };

  const handleEditModule = (module: Module) => {
    setModuleToEdit(module);
    setSelectedModule(null); // Cerrar drawer de detalles si está abierto
  };

  const handleCloseEditModule = () => {
    setModuleToEdit(null);
  };

  const handleUpdateModuleSuccess = (success: boolean, message: string) => {
    setUpdateNotificationConfig({
      title: success ? '¡Éxito!' : 'Error',
      message,
      type: success ? 'success' : 'error'
    });
    setShowUpdateNotification(true);
    
    if (success) {
      refetchModules();
      setModuleToEdit(null);
    }
  };

  const handleDeleteModule = (module: Module) => {
    setModuleToDelete(module);
  };

  const handleCloseDeleteModule = () => {
    setModuleToDelete(null);
  };

  const handleConfirmDeleteModule = async () => {
    if (!moduleToDelete) return;

    try {
      await ModulesService.deleteModule(moduleToDelete.id);
      
      setUpdateNotificationConfig({
        title: '¡Éxito!',
        message: 'Módulo eliminado exitosamente',
        type: 'success'
      });
      setShowUpdateNotification(true);
      
      refetchModules();
      setModuleToDelete(null);
    } catch (error: any) {
      setUpdateNotificationConfig({
        title: 'Error',
        message: error?.response?.data?.message || 'Error al eliminar el módulo',
        type: 'error'
      });
      setShowUpdateNotification(true);
    }
  };

  const handleToggleModuleStatus = async (module: Module) => {
    try {
      const updatedModule = await ModulesService.toggleModuleStatus(module.id);
      
      // Actualizar el módulo seleccionado si es el mismo que se modificó
      if (selectedModule && selectedModule.id === module.id) {
        setSelectedModule(updatedModule);
      }
      
      setUpdateNotificationConfig({
        title: '¡Éxito!',
        message: `Módulo ${updatedModule.activo ? 'activado' : 'desactivado'} exitosamente`,
        type: 'success'
      });
      setShowUpdateNotification(true);
      
      // Refrescar la lista
      refetchModules();
    } catch (error: any) {
      setUpdateNotificationConfig({
        title: 'Error',
        message: error?.response?.data?.message || 'Error al cambiar estado del módulo',
        type: 'error'
      });
      setShowUpdateNotification(true);
    }
  };

  // Manejo de paginación
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && (!meta || newPage <= meta.totalPages)) {
      setPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (meta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (meta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleActivoFilterChange = (activo: boolean | undefined) => {
    setActivoFilter(activo);
  };

  if (modulesError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xs font-medium text-gray-900 mb-4">Error al cargar módulos</h2>
          <p className="text-gray-600 mb-4">No se pudieron cargar los módulos. Por favor, intenta nuevamente.</p>
          <button 
            onClick={() => refetchModules()}
            className="bg-blue-600 text-white px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        {/* Filtros y barra de búsqueda */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Filtros de estado */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 font-medium">Filtrar por:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => handleActivoFilterChange(undefined)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activoFilter === undefined
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => handleActivoFilterChange(true)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activoFilter === true
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Activos
              </button>
              <button
                onClick={() => handleActivoFilterChange(false)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activoFilter === false
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Inactivos
              </button>
            </div>
          </div>
          
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isFetchingModules && searchTerm ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
              ) : (
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar módulos por nombre..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs placeholder-gray-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="text-xs">
                  {searchTerm ? (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-blue-700">{meta ? meta.total : modules.length}</span>
                      <span className="text-gray-600">resultados encontrados</span>
                      <svg className="w-4 h-4 text-blue-500 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-blue-700">{meta ? meta.total : modules.length}</span>
                      <span className="text-gray-600">módulos registrados</span>
                      <svg className="w-4 h-4 text-blue-500 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Selector de cantidad por página */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500 font-medium">Mostrar</span>
              <div className="relative" data-dropdown>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-xs font-medium text-gray-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer flex items-center min-w-15"
                >
                  {limit}
                </button>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    {[7, 10, 20, 30, 50].map((value) => (
                      <div
                        key={value}
                        onClick={() => {
                          handleLimitChange(value);
                          setIsDropdownOpen(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={handleCreateModule}
              className="bg-blue-600 text-white px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Nuevo Módulo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de módulos */}
      <div className="relative">
        <ModulesTableSimple
          modules={modules}
          isLoading={isLoadingModules && modules.length === 0}
          onViewModule={handleViewModule}
        />
        {isFetchingModules && modules.length > 0 && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-6 py-4 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-xs font-medium text-gray-700">Buscando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Paginación */}
      {meta && (
        <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
          <div className="flex items-center justify-between">
            {/* Información de resultados */}
            <div className="flex items-center space-x-6">
              <div className="text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {((meta.page - 1) * meta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(meta.page * meta.limit, meta.total)}
                </span>
                <span className="mx-2">de</span>
                <span className="font-medium text-gray-900">{meta.total}</span>
                <span className="ml-1">resultados</span>
              </div>
              
              {/* Páginas X de Y */}
              <div className="text-xs text-gray-500">
                Página <span className="font-medium text-gray-900">{meta.page}</span> de <span className="font-medium text-gray-900">{meta.totalPages}</span>
              </div>
            </div>
            
            {/* Controles de paginación */}
            <div className="flex items-center space-x-1">
              {/* Botón primera página */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={!meta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Botón página anterior */}
              <button
                onClick={handlePreviousPage}
                disabled={!meta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Páginas centrales */}
              <div className="flex items-center space-x-1 mx-2">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 3;
                  let start = Math.max(1, meta.page - Math.floor(maxVisiblePages / 2));
                  let end = Math.min(meta.totalPages, start + maxVisiblePages - 1);
                  
                  start = Math.max(1, end - maxVisiblePages + 1);

                  // Mostrar primera página si no está en el rango
                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                  }

                  // Páginas del rango central
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                          i === meta.page
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Mostrar última página si no está en el rango
                  if (end < meta.totalPages) {
                    if (end < meta.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                    pages.push(
                      <button
                        key={meta.totalPages}
                        onClick={() => handlePageChange(meta.totalPages)}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {meta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Botón página siguiente */}
              <button
                onClick={handleNextPage}
                disabled={!meta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Botón última página */}
              <button
                onClick={() => handlePageChange(meta.totalPages)}
                disabled={!meta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast para actualizaciones */}
      {updateNotificationConfig && (
        <NotificationToast
          isOpen={showUpdateNotification}
          title={updateNotificationConfig.title}
          message={updateNotificationConfig.message}
          type={updateNotificationConfig.type}
          onClose={() => {
            setShowUpdateNotification(false);
            setUpdateNotificationConfig(null);
          }}
          duration={7000}
        />
      )}

      {/* Drawer de detalles del módulo */}
      <ModuleDetailsDrawer 
        isOpen={selectedModule !== null}
        module={selectedModule}
        onClose={handleCloseModuleDetails}
        onEditModule={handleEditModule}
        onToggleModuleStatus={handleToggleModuleStatus}
        onDeleteModule={handleDeleteModule}
      />

      {/* Drawer de crear módulo */}
      <CreateModuleDrawer 
        isOpen={isCreateModuleOpen}
        onClose={handleCloseCreateModule}
        onSuccess={handleCreateModuleSuccess}
      />

      {/* Drawer de editar módulo */}
      <EditModuleDrawer 
        isOpen={moduleToEdit !== null}
        module={moduleToEdit}
        onClose={handleCloseEditModule}
        onSuccess={() => refetchModules()}
        onUpdateResult={handleUpdateModuleSuccess}
      />

      {/* Modal de confirmación de eliminación */}
      {moduleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xs font-medium text-gray-900 mb-4">
              ¿Eliminar módulo?
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar el módulo <strong>{moduleToDelete.nombre}</strong>? 
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteModule}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDeleteModule}
                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulesPage;